import useMute from 'hooks/useMute'
import useBlock from 'hooks/useBlock'
import { Action } from 'components/ActionsDropdown/types'
import useActionGuard from 'hooks/useActionGuard'
import { feedsSlice } from 'components/feed/feedsSlice'
import { useDispatch } from 'react-redux'
import { User, Relationship } from '@wellesley/openapi'
import { UserX, BellOff, Ban } from 'lucide-react'
import { getUserNameWithDomain } from 'helpers/getUserNameWithDomain'
import {
  canUnsubscribeFromMe,
  usePostApiV1UsersFollowByUsernameRejectMutation,
} from '@wellesley/user'

type HookProps = {
  user: User
  relationship: Relationship
  isOwnProfile: boolean
}

const commonIconProps = {
  size: 20,
  color: 'var(--gray-900)',
}

const useFollowControlActions = ({
  user,
  relationship,
  isOwnProfile,
}: HookProps) => {
  const dispatch = useDispatch()

  const [isBlocked, block, unblock, renderBlockDialog] = useBlock({
    user,
    relationship,
  })

  const { isMuted, mute, unmute, renderMuteDialog } = useMute({
    user,
    relationship,
    onUnmute: () => {
      dispatch(feedsSlice.actions.showUserPosts(user.id))
    },
  })

  const [rejectFollower] = usePostApiV1UsersFollowByUsernameRejectMutation()

  const { isDisabled, ActionTooltipWrapper } = useActionGuard()

  const actions = [
    {
      icon: <UserX {...commonIconProps} />,
      name: 'Remove from followers',
      handler: () =>
        void rejectFollower({
          username: getUserNameWithDomain(user),
        }),
      isHidden: !canUnsubscribeFromMe(relationship) || isOwnProfile,
      disabled: isDisabled,
    },
    {
      icon: <BellOff {...commonIconProps} />,
      name: isMuted ? 'Unmute' : 'Mute',
      handler: isMuted ? unmute : mute,
      isHidden: isOwnProfile || isBlocked,
      disabled: isDisabled,
      renderDialog: renderMuteDialog,
    },
    {
      icon: <Ban {...commonIconProps} color="var(--red-500)" />,
      name: isBlocked ? 'Unblock' : 'Block',
      handler: isBlocked ? unblock : block,
      className: 'text-red-500',
      isHidden: isOwnProfile,
      disabled: isDisabled,
      renderDialog: renderBlockDialog,
    },
  ] as Action[]

  return [actions, ActionTooltipWrapper] as const
}

export default useFollowControlActions
